import { Button, PageHeader, Skeleton, Surface } from '../components/ui/Primitives';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import PhoneSection from '../components/PhoneSection';
import TranscriptSection from '../components/TranscriptSection';
import MetricsDashboard from '../components/SettingsComponents/MetricsDashboard';
import { logEvent } from '../utils/logger';
import flashMessage from '../components/HelperFunctions';
import OnboardingSetupPlan from '../components/OnboardingSetupPlan';

const MODAL_PRESETS = [
  { id: '01_call_feedback', heading: 'Quick check-in', question: 'What stood out to you about how Sayable handled its first call?', placeholder: 'One sentence is fine' },
  { id: '02_call_feedback', heading: 'Quick check-in', question: 'What almost stopped you from using Sayable?', placeholder: 'One sentence is fine' },
];

function Dashboard({ orgMember, organization }) {
  const [updatedUser, setUpdatedUser] = useState(orgMember);
  const [updatedOrganization, setUpdatedOrganization] = useState(organization);
  const [isLoading, setIsLoading] = useState(true);
  const [accountCallsCount, setAccountCallsCount] = useState(0);
  const [showAutoModal, setShowAutoModal] = useState(false);
  const [modalAnswer, setModalAnswer] = useState('');
  const [isSubmittingModal, setIsSubmittingModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const navigate = useNavigate();
  const activeModal = useMemo(() => MODAL_PRESETS[0], []);

  useEffect(() => {
    logEvent({ event: { label: 'PageView', data: { page: 'Overview', user_id: orgMember?.user_id || null } } });

  }, [orgMember?.user_id]);

  useEffect(() => {
    setUpdatedUser(orgMember);
    setUpdatedOrganization(organization);
    setIsLoading(!orgMember || !organization);
  }, [orgMember, organization]);

  useEffect(() => {
    const seen = updatedOrganization?.accepted_dialog?.[activeModal.id]?.seen;
    if (!isLoading && updatedUser && accountCallsCount > 0 && !seen) setShowAutoModal(true);
  }, [accountCallsCount, activeModal.id, isLoading, updatedOrganization, updatedUser]);

  async function submitFeedback(answer, source) {
    const jwtToken = localStorage.getItem('jwt_authorization');
    if (!jwtToken) { navigate('/signin'); return false; }
    await axios.post(`${process.env.REACT_APP_BACKEND_HOST}/feedback`, {
      jwt_token: jwtToken,
      feedback: JSON.stringify(source === 'dashboard_modal'
        ? { source, modal_id: activeModal.id, heading: activeModal.heading, question: activeModal.question, answer }
        : { source, heading: 'User feedback', answer }),
    });
    return true;
  }

  async function sendClicked() {
    setIsSendingFeedback(true);
    try {
      await submitFeedback(feedback, 'feedback_section');
      flashMessage('success', 'Feedback sent successfully');
      setFeedback('');
    } catch (error) {
      flashMessage('error', 'Feedback could not be sent');
    } finally {
      setIsSendingFeedback(false);
    }
  }

  async function handleModalSkip() {
    setShowAutoModal(false);
    setModalAnswer('');
    try { await submitFeedback('Skipped', 'dashboard_modal'); } catch { /* optional prompt */ }
  }

  async function handleModalSubmit() {
    setIsSubmittingModal(true);
    try {
      await submitFeedback(modalAnswer, 'dashboard_modal');
      flashMessage('success', 'Submitted. Thank you.');
      setModalAnswer('');
      setShowAutoModal(false);
    } catch (error) {
      flashMessage('error', 'Response could not be submitted');
    } finally {
      setIsSubmittingModal(false);
    }
  }

  if (isLoading || !updatedUser || !updatedOrganization) {
    return <div className="page-content dashboard-page dashboard-loading"><Skeleton width="42%" height={42} /><Skeleton height={150} /><Skeleton height={210} /></div>;
  }

  return (
    <div className="page-content dashboard-page">
      {showAutoModal && (
        <div className="dashboard-modal-backdrop" role="presentation" onMouseDown={handleModalSkip}>
          <div className="dashboard-modal" role="dialog" aria-modal="true" aria-labelledby="dashboard-modal-title" onMouseDown={(event) => event.stopPropagation()}>
            <span className="say-eyebrow">Help shape Sayable</span>
            <h2 id="dashboard-modal-title">{activeModal.heading}</h2>
            <p>{activeModal.question}</p>
            <textarea value={modalAnswer} onChange={(event) => setModalAnswer(event.target.value)} rows={4} placeholder={activeModal.placeholder} autoFocus />
            <div className="dashboard-modal-actions">
              <Button variant="secondary" onClick={handleModalSkip}>Skip</Button>
              <Button onClick={handleModalSubmit} disabled={!modalAnswer.trim() || isSubmittingModal} loading={isSubmittingModal} loadingLabel={"Submitting…"}>{'Submit feedback'}</Button>
            </div>
          </div>
        </div>
      )}

      <PageHeader
        eyebrow="Overview"
        title={`Welcome back, ${updatedUser.first_name || 'there'}`}
        description="See how your AI receptionist is performing and keep the experience ready for every caller."
        actions={<><Button to="/receptionist">Manage receptionist</Button><Button to="/knowledge" variant="secondary">Improve knowledge</Button><Button to="/calls" variant="secondary">View all calls</Button></>}
      />

      <OnboardingSetupPlan />

      <PhoneSection user={updatedUser} organization={updatedOrganization} />
      <MetricsDashboard user={updatedUser} setAccountCallsCount={setAccountCallsCount} />

      <div className="dashboard-lower-grid">
        <TranscriptSection />
        <Surface className="feedback-section">
          <span className="say-eyebrow">Your feedback</span>
          <h2>Help us make every call better</h2>
          <p>Tell us what is working or where the receptionist could improve. For support, email <a href="mailto:support@sayable.ai">support@sayable.ai</a>.</p>
          <textarea className="feedback-textarea" placeholder="Share your feedback" rows="5" value={feedback} onChange={(event) => setFeedback(event.target.value)} />
          <Button className="feedback-button" onClick={sendClicked} disabled={!feedback.trim() || isSendingFeedback} loading={isSendingFeedback} loadingLabel={"Sending…"}>{'Send feedback'}</Button>
        </Surface>
      </div>
    </div>
  );
}

export default Dashboard;
